// Operations -> DISA Annual (George 2026-11-01). DISA's monthly "Annual DCCHT Test
// Renewal Report" from the Outlook OccMed folder, mirrored by the MinisForum OccMed
// saver: people coming up on 365 days without a test of that type. Any test of the same
// type resets the clock (random, pre-access, annual); hair and urine never count for each
// other, and 365 days without one drops the person from the pool. Renewed comes from a
// later report row or a DISA certificate of the same type; the DISA roster's newest test
// carries no specimen type, so without a certificate it only reads Check test. Admin and
// leadership only, server-enforced.

import { useEffect, useState } from 'react';
import { api } from '../api';
import { styles, colors, fmtDate, fmtDateTime, chipBtn } from './UI';
import { CurrentJob } from './DisaRandomsTab';

const STATUS_COLORS = {
  Overdue: [colors.redSoft, '#991B1B'],
  'Check test': ['#FEF9C3', '#854D0E'],
  'Due soon': [colors.amberSoft, '#92400E'],
  Upcoming: [colors.blueSoft, colors.blue],
  Renewed: [colors.greenSoft, '#065F46'],
  'Not required': [colors.graySoft, colors.textMuted],
  'Not employed': [colors.graySoft, colors.textMuted],
};
const RESOLVED = ['Not required', 'Not employed'];
const ORDER = ['Overdue', 'Check test', 'Due soon', 'Upcoming', 'Renewed', 'Not required', 'Not employed'];
const daysBetween = (a, b) => Math.round((new Date(`${b}T12:00:00`) - new Date(`${a}T12:00:00`)) / 86400000);

export default function DisaAnnualTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(null);   // a status: only rows in it are listed

  const load = () => api.disaAnnualList().then(d => { setData(d); setErr(''); }).catch(e => setErr(e.message));
  useEffect(() => { load(); }, []);

  const act = async (fn, ok) => {
    setBusy(true); setMsg('');
    try { const r = await fn(); if (ok) setMsg(ok(r)); await load(); }
    catch (e) { setMsg(`⚠ ${e.message}`); }
    finally { setBusy(false); }
  };
  const save = (r, payload, ok) => act(() => api.disaAnnualUpdate(r.id, payload), ok);
  const saveNotes = (r) => save(r, { notes: notes[r.id] ?? r.notes ?? '' }, () => {
    setNotes(n => { const c = { ...n }; delete c[r.id]; return c; });
    return `${r.first_name} ${r.last_name}: notes saved`;
  });
  const sync = () => act(() => api.disaRandomsSync(), r => r.ok
    ? (r.skipped ? 'Feed unchanged since the last load' : `Loaded ${r.renewals} renewal rows`)
    : `⚠ ${r.error}`);

  if (err) return <div style={styles.errorBox}>{err}</div>;
  if (!data) return <div style={styles.card}>Loading DISA annual renewals…</div>;
  const s = data.sync || {};
  const all = [...data.renewals].sort((a, b) =>
    ORDER.indexOf(a.status) - ORDER.indexOf(b.status) || a.due_on.localeCompare(b.due_on) || a.last_name.localeCompare(b.last_name));
  // Renewed rows are history once the person is retested; hidden unless asked for.
  const rows = show ? all.filter(r => r.status === show) : all.filter(r => r.status !== 'Renewed');
  const counts = ORDER.map(st => [st, all.filter(r => r.status === st).length]);

  return (
    <div style={styles.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <div>
          <h2 style={{ margin: 0, color: colors.charcoal }}>📅 DISA Annual Hair Test Renewals</h2>
          <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4, maxWidth: 820 }}>
            Mirrored from DISA's monthly Annual DCCHT Test Renewal Report in the OccMed folder. 365 days without a test of the
            same type (DCCHT = hair) drops the person from the pool; any hair test resets it, whatever the purpose, and a urine
            test never does. Check test = past due, but the DISA roster shows a newer test with no certificate here to say
            whether it was hair: confirm in DISAWorks.
            {' '}Last loaded {s.last_ok_at ? fmtDateTime(s.last_ok_at) : 'never'}
            {s.report_modified ? ` · feed written ${fmtDateTime(s.report_modified)}` : ''}.
            {s.last_status === 'error' && <span style={{ color: colors.red, fontWeight: 600 }}> Last sync failed: {s.last_error}</span>}
          </div>
        </div>
        <button style={styles.btnSecondary} disabled={busy} onClick={sync}>⟳ Sync now</button>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8, fontSize: 12 }}>
        <button onClick={() => setShow(null)} title="Everyone still to renew"
          style={chipBtn(colors.graySoft, colors.charcoal, !show)}>
          Open {all.filter(r => r.status !== 'Renewed').length}
        </button>
        {counts.filter(([, n]) => n).map(([st, n]) => (
          <button key={st} onClick={() => setShow(show === st ? null : st)} title={show === st ? 'Show open rows' : `Show only ${st}`}
            style={chipBtn(STATUS_COLORS[st][0], STATUS_COLORS[st][1], show === st)}>
            {st} {n}
          </button>
        ))}
      </div>
      {msg && <div style={{ fontSize: 13, margin: '6px 0', color: msg.startsWith('⚠') ? colors.red : '#065F46' }}>{msg}</div>}
      {!rows.length ? (
        <div style={{ color: colors.textMuted, padding: 12 }}>
          {data.renewals.length ? 'Nothing in this view.' : 'No DISA renewal reports loaded yet. The OccMed saver runs hourly on the MinisForum.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                {['Employee', 'SSN', 'Policy', 'Last test', 'Due (365 days)', 'Status', 'Job now', 'Disposition', 'Notes'].map(h => <th key={h} style={styles.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const [bg, fg] = STATUS_COLORS[r.status] || STATUS_COLORS.Upcoming;
                const left = daysBetween(data.today, r.due_on);
                return (
                  <tr key={r.id}>
                    <td style={styles.td}>
                      <div style={{ fontWeight: 600 }}>{r.first_name} {r.last_name}</div>
                      {r.employee_name
                        ? <div style={{ fontSize: 11, color: '#065F46' }}>Roster: {r.employee_name}</div>
                        : <div style={{ fontSize: 11, color: colors.textMuted }}>not matched to the Roster</div>}
                      {r.employee_phone && <div style={{ fontSize: 11 }}><a href={`tel:${r.employee_phone.replace(/[^\d+]/g, '')}`}>{r.employee_phone}</a></div>}
                      {r.employee_email && <div style={{ fontSize: 11, wordBreak: 'break-all' }}><a href={`mailto:${r.employee_email}`}>{r.employee_email}</a></div>}
                    </td>
                    <td style={styles.td}>{r.ssn_last4 ? `…${r.ssn_last4}` : ''}</td>
                    <td style={{ ...styles.td, fontSize: 12 }}>{r.policy}</td>
                    <td style={styles.td}>
                      {fmtDate(r.last_test_on)}
                      {r.other_client && <div style={{ fontSize: 11, color: colors.textMuted }}>taken with another client</div>}
                      {r.roster_test_on && r.roster_test_on > r.last_test_on && !r.renewed && (
                        <div style={{ fontSize: 11, color: '#92400E' }} title="The DISA roster's latest drug test. Only purpose Annual counts as the renewal; any other may be urine">
                          Newer test {fmtDate(r.roster_test_on)}{r.roster_test_purpose ? ` (${r.roster_test_purpose})` : ''}
                          {r.roster_cert
                            ? <div style={{ color: colors.textMuted }}>{[r.roster_cert.ccf_id, r.roster_cert.policy].filter(Boolean).join(' · ')}{r.roster_cert.same_type === false ? ': other test type, does not reset' : r.roster_cert.same_type === null ? ': certificate names no policy, confirm the test type in DISAWorks' : ''}</div>
                            : <div style={{ color: colors.textMuted }}>no DISA certificate here: confirm the test type in DISAWorks</div>}
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>
                      {fmtDate(r.due_on)}
                      {!r.renewed && !RESOLVED.includes(r.status) && <div style={{ fontSize: 11, fontWeight: 600, color: left < 0 ? (r.status === 'Overdue' ? colors.red : '#854D0E') : left <= 30 ? '#92400E' : colors.textMuted }}>
                        {left < 0 ? `${-left} day${left === -1 ? '' : 's'} past due` : left === 0 ? 'due today' : `${left} day${left === 1 ? '' : 's'} left`}
                      </div>}
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.pill, background: bg, color: fg, whiteSpace: 'nowrap' }}>{r.status}</span>
                      {r.renewed && <div style={{ fontSize: 11, color: '#065F46', marginTop: 3 }}>{fmtDate(r.renewed.on)} · {r.renewed.source}{r.renewed.ccf_id ? ` ${r.renewed.ccf_id}` : ''}<div>next due {fmtDate(r.renewed.next_due)}</div></div>}
                    </td>
                    <td style={{ ...styles.td, minWidth: 150 }}>
                      {r.employee_id ? <CurrentJob jobs={r.jobs} /> : <span style={{ fontSize: 12, color: colors.textMuted }}>—</span>}
                    </td>
                    <td style={styles.td}>
                      {r.renewed ? (
                        <span style={{ fontSize: 12, color: colors.textMuted }}>Not needed</span>
                      ) : (
                        <select style={{ ...styles.input, padding: '3px 6px', width: 'auto' }} disabled={busy} value={r.disposition || ''}
                          onChange={e => save(r, { disposition: e.target.value || null })} title="Not employed applies to this person on every DISA tab (Randoms, Annual, Roster Changes), every month">
                          <option value="">—</option>
                          {data.dispositions.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                      )}
                    </td>
                    <td style={{ ...styles.td, minWidth: 180 }}>
                      <textarea style={{ ...styles.textarea, minHeight: 34, fontSize: 12 }} value={notes[r.id] ?? r.notes ?? ''}
                        onChange={e => setNotes(n => ({ ...n, [r.id]: e.target.value }))} />
                      {notes[r.id] !== undefined && (
                        <button style={{ ...styles.btnSecondary, padding: '2px 8px', fontSize: 12 }} disabled={busy} onClick={() => saveNotes(r)}>Save notes •</button>
                      )}
                      {r.updated_at && <div style={{ fontSize: 10, color: colors.textMuted }}>edited {fmtDateTime(r.updated_at)}{r.updated_by_name ? ` by ${r.updated_by_name}` : ''}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
